import React, { useState } from 'react';
import { ASSET_URL } from '../../../../helper/api_url';
import { formatDate } from '../../../../helper/formatDate';
import { getTrialStatus, getStatusBadge, openGoogleMap, truncateText, getWhatsAppAppUrl, getWhatsAppWebUrl, getServiceBadgeClass } from './enquiryUtils';
import { AddressModal } from './AddressModal';
import { UpateEnquiryModal } from '../UpateEnquiryModal';
import { Button, Dropdown } from 'react-bootstrap';

export const EnquiryMobileCard = ({
    row,
    enquirys,
    authData,
    handleEnquiryDelete,
    handleReport
}) => {
    const [showAddressModal, setShowAddressModal] = useState(false);

    const imageUrl = ASSET_URL + row.avatar;
    const trialStatus = getTrialStatus(row.tryal_date, row.tryal_period);
    const status = getStatusBadge(row.status);
    const address = row.address_1 || row.address || '';
    const truncated = truncateText(address, 30);
    const appUrl = getWhatsAppAppUrl(row.whatsapp, row.phone, row.name);
    const webUrl = getWhatsAppWebUrl(row.whatsapp, row.phone, row.name);
    const hasPhone = Boolean(appUrl || webUrl);
    const createdByName = row.created_by
        ? `${row.created_by.first_name} ${row.created_by.last_name || ''}`.trim()
        : 'System';

    return (
        <div className="card shadow-sm border border-light rounded-3 mb-3 w-100 enquiry-mobile-card">
            <div className="card-body p-3">
                {/* 1. Header: Avatar with Status Bubble + Bold School Name + Trial status */}
                <div className="d-flex align-items-start justify-content-between mb-2 gap-2">
                    <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                        <div className="avatar-status-wrapper flex-shrink-0">
                            <img className="rounded-circle border" style={{ width: '42px', height: '42px', objectFit: 'cover' }} src={imageUrl} alt="Avatar" />
                            <span className={`status-bubble status-bubble-${status.color}`} title={`Status: ${status.label}`} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <h6 className="fs-14 mb-0 fw-bold text-dark text-truncate">{row.name}</h6>
                            <div className="d-flex flex-wrap gap-1 mt-1 align-items-center">
                                {row.course?.name && (
                                    <span className={`service-pill-badge ${getServiceBadgeClass(row.course.name)} fs-10 px-2 py-0.5`}>
                                        {row.course.name}
                                    </span>
                                )}
                                {trialStatus && (
                                    <span className={`badge ${trialStatus.badgeClass} fs-10 px-2 py-0.5`}>
                                        {trialStatus.label}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. Address & District + Google Map Icon */}
                <div className="p-2 bg-light-subtle rounded border my-2">
                    <div className="fs-12 text-muted lh-sm">
                        <i className="ri-home-4-line me-1 text-muted"></i>
                        {address ? (
                            <span>
                                {truncated.displayText}
                                {truncated.isTruncated && (
                                    <span className="badge bg-soft-primary text-primary ms-1 cursor-pointer address-dot-trigger" onClick={() => setShowAddressModal(true)} title="Click to view full address">
                                        ...
                                    </span>
                                )}
                            </span>
                        ) : (
                            <span className="fst-italic text-muted">No address provided</span>
                        )}
                    </div>

                    <div className="d-flex align-items-center justify-content-between mt-1 pt-1 border-top border-light">
                        <small className="text-secondary fs-11 d-flex align-items-center gap-1">
                            <i className="ri-building-line text-muted"></i>
                            <span className="fw-medium">{row.district || 'District N/A'}</span>
                        </small>
                        {row.feedback && (
                            <button type="button" className="btn btn-sm btn-link text-danger p-0 d-inline-flex align-items-center gap-1 fs-11 text-decoration-none" onClick={() => openGoogleMap(row.feedback)} title="Open Google Map">
                                <i className="ri-map-pin-2-fill fs-13"></i>
                                <span>Map</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* 3. Details Row: Principal / Phone and Date & By */}
                <div className="py-2 border-top border-bottom border-light my-2">
                    <div className="row g-2">
                        <div className="col-6">
                            <small className="text-muted d-block fs-11">Principal / Phone</small>
                            {row.father_name && (
                                <span className="text-dark fs-12 fw-semibold d-block text-truncate" title={`Principal: ${row.father_name}`}>
                                    <i className="ri-user-star-line me-1 text-muted fs-12" />{row.father_name}
                                </span>
                            )}
                            <a href={`tel:${row.phone}`} className="text-success fs-12 fw-semibold d-inline-flex align-items-center mt-0.5">
                                <i className="bx bx-phone me-1 fs-13" />{row.phone}
                            </a>
                        </div>

                        <div className="col-6">
                            <small className="text-muted d-block fs-11">Date & By</small>
                            <span className="fw-bold text-dark fs-12 d-block">
                                <i className="ri-calendar-2-line me-1 text-muted fs-12" />{formatDate(row.created_at)}
                            </span>
                            <small className="text-muted fs-11 d-block text-truncate">
                                <i className="ri-user-line me-1" />{createdByName}
                            </small>
                        </div>
                    </div>
                </div>

                {/* 4. Actions Footer: WhatsApp, Edit, Report, Delete */}
                <div className="d-flex align-items-center justify-content-between pt-1">
                    <div>
                        {authData?.role?.priority >= 50 && (
                            <button
                                className="btn btn-sm btn-soft-info waves-effect d-inline-flex align-items-center py-1 px-2 fs-11"
                                onClick={() => handleReport(row)}
                            >
                                <i className="bx bx-history me-1 fs-12" />
                                <span>Report</span>
                            </button>
                        )}
                    </div>
                    <div className="d-flex gap-1 align-items-center">
                        {/* WhatsApp Dropdown (App vs Web) */}
                        {hasPhone && (
                            <Dropdown align="end" className="d-inline-block">
                                <Dropdown.Toggle
                                    variant="soft-success"
                                    size="sm"
                                    className="py-1 px-2 border-0 arrow-none d-inline-flex align-items-center justify-content-center"
                                    style={{ height: '28px' }}
                                    title="Chat on WhatsApp"
                                >
                                    <i className="ri-whatsapp-line fs-14 text-success" />
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="shadow border-0 py-1" style={{ minWidth: '185px', zIndex: 1050 }}>
                                    <Dropdown.Item href={appUrl} className="d-flex align-items-center gap-2 py-1.5 fs-12 text-dark">
                                        <i className="ri-smartphone-line text-success fs-15" />
                                        <div>
                                            <span className="fw-semibold d-block">WhatsApp App</span>
                                            <small className="text-muted fs-11">Open Desktop / Phone App</small>
                                        </div>
                                    </Dropdown.Item>
                                    <Dropdown.Divider className="my-1" />
                                    <Dropdown.Item href={webUrl} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center gap-2 py-1.5 fs-12 text-dark">
                                        <i className="ri-global-line text-primary fs-15" />
                                        <div>
                                            <span className="fw-semibold d-block">WhatsApp Web</span>
                                            <small className="text-muted fs-11">Open in Browser</small>
                                        </div>
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        )}

                        <UpateEnquiryModal data={row} apiHandler={enquirys} />

                        <Button
                            onClick={() => handleEnquiryDelete(row.id)}
                            className="btn btn-sm btn-soft-danger py-1 px-2 d-inline-flex align-items-center justify-content-center"
                            style={{ height: '28px' }}
                            title="Delete Enquiry"
                        >
                            <i className="ri-delete-bin-fill fs-12" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Address Modal */}
            {showAddressModal && (
                <AddressModal
                    show={showAddressModal}
                    onHide={() => setShowAddressModal(false)}
                    schoolName={row.name}
                    address={address}
                    district={row.district}
                    mapUrl={row.feedback}
                />
            )}
        </div>
    );
};

export default EnquiryMobileCard;

